import React, { useState, useMemo } from 'react';
import { X, Plus, Trash2, FlaskConical, RotateCcw, Target, ArrowRight, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { ScoreBreakdown } from './ScoreBreakdown';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Scoring Engine (mirrors backend _compute_fit_score exactly) ──

const SENIORITY_LEVELS = { entry: 1, junior: 1, mid: 2, senior: 3, manager: 4, director: 5, vp: 6 };

const FUNCTION_WORDS = [
  'analyst', 'manager', 'director', 'coordinator', 'specialist',
  'administrator', 'supervisor', 'lead', 'buyer', 'nurse',
  'practitioner', 'therapist', 'technologist', 'technician', 'rn', 'cna', 'np',
];

const TITLE_STOP = new Set(['the', 'and', 'for', 'with', 'senior', 'junior', 'lead', 'chief', 'staff', 'principal']);

const DEPT_KEYWORDS = {
  accounting_finance: ['accounting', 'finance', 'financial', 'audit'],
  healthcare: ['healthcare', 'medical', 'nursing', 'clinical'],
  logistics_supply_chain: ['logistics', 'supply chain', 'warehouse', 'operations'],
  hr_compliance: ['human resources', 'hr', 'recruiting', 'compliance', 'talent'],
};

const VERTICAL_LABELS = {
  accounting_finance: 'Accounting & Finance',
  healthcare: 'Healthcare',
  logistics_supply_chain: 'Logistics & Supply Chain',
  hr_compliance: 'HR & Compliance',
};

const DEFAULT_WEIGHTS = {
  title_match: { exact: 45, near: 35, partial: 20, related: 12, weak: 8 },
  function_match: { match: 5, mismatch: -8 },
  seniority: { exact: 15, close: 6, far: -10, unknown: -3 },
  location: { city: 15, state: 10 },
  keywords: { per_keyword: 3, max: 8 },
  industry: { match: 10 },
};

function extractFunctions(title) {
  const lower = title.toLowerCase();
  return FUNCTION_WORDS.filter(fw => new RegExp(`\\b${fw}\\b`, 'i').test(lower));
}

function titleWords(title) {
  return title.split(/[\s\-/,()]+/).filter(w => w.length > 2 && !TITLE_STOP.has(w.toLowerCase())).map(w => w.toLowerCase());
}

function simulateScore(role, candidate, weights) {
  const w = weights || DEFAULT_WEIGHTS;
  let score = 0;
  const breakdown = [];

  const candTitle = (candidate.title || '').trim();
  const candTitleLower = candTitle.toLowerCase();
  const candCity = (candidate.city || '').toLowerCase();
  const candState = (candidate.state || '').toLowerCase();
  const candSeniority = (candidate.seniority || '').toLowerCase();
  const candDept = (candidate.department || '').toLowerCase();
  const roleTitle = (role.title || '').trim();
  const roleTitleLower = roleTitle.toLowerCase();

  // ── 1. TITLE MATCH ──
  let titlePts = 0, titleMax = w.title_match.exact, titleCat = 'none', titleDetail = '', titleScored = false;
  if (roleTitleLower && candTitleLower) {
    const rWords = new Set(titleWords(roleTitle));
    const cWords = new Set(titleWords(candTitle));
    if (roleTitleLower === candTitleLower) {
      titlePts = w.title_match.exact; titleCat = 'exact';
      titleDetail = `Exact match: "${candTitle}"`; titleScored = true;
    } else if (rWords.size > 0 && cWords.size > 0) {
      const overlap = [...rWords].filter(w => cWords.has(w));
      const ratio = overlap.length / Math.max(rWords.size, 1);
      if (ratio >= 0.7) {
        titlePts = w.title_match.near; titleCat = 'near';
        titleDetail = `Near match (${Math.round(ratio * 100)}%): "${candTitle}"`; titleScored = true;
      } else if (ratio >= 0.4) {
        titlePts = w.title_match.partial; titleCat = 'partial';
        titleDetail = `Partial match (${Math.round(ratio * 100)}%): "${candTitle}"`; titleScored = true;
      }
    }
  }
  if (!titleScored) {
    const keywords = (role.keywords || '').split(',').map(k => k.trim()).filter(Boolean);
    const kwHits = keywords.filter(kw => candTitleLower.includes(kw.toLowerCase())).length;
    if (kwHits > 0) {
      titlePts = Math.min(w.title_match.weak, kwHits * 4); titleCat = 'weak';
      titleDetail = `Weak keyword overlap (${kwHits} keywords)`; titleScored = true;
    } else {
      titleDetail = `No match: "${candTitle.slice(0, 30)}" vs "${roleTitle.slice(0, 30)}"`;
    }
  }
  let funcPts = 0, funcDetail = '';
  if (roleTitle && candTitle) {
    const roleFns = new Set(extractFunctions(roleTitle));
    const candFns = new Set(extractFunctions(candTitle));
    if (roleFns.size > 0 && candFns.size > 0) {
      const common = [...roleFns].filter(f => candFns.has(f));
      if (common.length > 0) {
        funcPts = w.function_match.match;
        funcDetail = ` + Function match (${common.join('/')})`;
      } else {
        funcPts = w.function_match.mismatch;
        funcDetail = ` - Function mismatch (${[...candFns].join('/')} vs ${[...roleFns].join('/')})`;
      }
    }
  }
  const combinedTitle = titlePts + funcPts;
  score += combinedTitle;
  breakdown.push({
    dimension: 'Title Match', points: combinedTitle, max_points: titleMax + w.function_match.match,
    category: titleCat, detail: titleDetail + funcDetail,
    comparison: { candidate: candTitle || '—', required: roleTitle || '—' },
  });

  // ── 2. SENIORITY ──
  let senPts = 0, senMax = w.seniority.exact, senCat = 'n/a', senDetail = '';
  const targetSen = (role.seniority || '').toLowerCase();
  if (targetSen && candSeniority) {
    const tLvl = SENIORITY_LEVELS[targetSen] || 2;
    const cLvl = SENIORITY_LEVELS[candSeniority] || 2;
    const diff = Math.abs(tLvl - cLvl);
    if (diff === 0) { senPts = w.seniority.exact; senCat = 'exact'; senDetail = `Exact: ${candSeniority}`; }
    else if (diff === 1) { senPts = w.seniority.close; senCat = 'close'; senDetail = `Close: ${candSeniority} (target: ${targetSen})`; }
    else { senPts = w.seniority.far; senCat = 'mismatch'; senDetail = `Mismatch: ${candSeniority} vs ${targetSen} (${diff} levels)`; }
  } else if (targetSen && !candSeniority) {
    senPts = w.seniority.unknown; senCat = 'unknown'; senDetail = `Unknown (target: ${targetSen})`;
  } else { senDetail = 'No seniority requirement'; }
  score += senPts;
  breakdown.push({
    dimension: 'Seniority', points: senPts, max_points: senMax, category: senCat,
    detail: senDetail, comparison: { candidate: candSeniority || 'unknown', required: targetSen || 'any' },
  });

  // ── 3. LOCATION ──
  let locPts = 0, locMax = w.location.city, locCat = 'n/a', locDetail = '';
  const roleLoc = (role.location || '').toLowerCase();
  if (roleLoc && roleLoc !== 'remote') {
    if (candCity && roleLoc.includes(candCity)) {
      locPts = w.location.city; locCat = 'exact'; locDetail = `City match: ${candCity}`;
    } else if (candState && roleLoc.includes(candState)) {
      locPts = w.location.state; locCat = 'nearby'; locDetail = `State match: ${candState.toUpperCase()}`;
    } else {
      locCat = 'none'; const cl = candCity ? `${candCity}, ${candState}` : candState || 'Unknown';
      locDetail = `No match: ${cl} vs ${roleLoc}`;
    }
  } else if (roleLoc === 'remote') { locCat = 'remote'; locDetail = 'Remote — location not scored'; }
  else { locDetail = 'No location requirement'; }
  score += locPts;
  breakdown.push({
    dimension: 'Location', points: locPts, max_points: locMax, category: locCat,
    detail: locDetail, comparison: { candidate: candCity ? `${candCity}, ${candState}` : candState || '—', required: roleLoc || 'any' },
  });

  // ── 4. SKILLS/KEYWORDS ──
  let kwPts = 0, kwMax = w.keywords.max, kwCat = 'n/a', kwDetail = '';
  if (titleScored) {
    const keywords = (role.keywords || '').split(',').map(k => k.trim()).filter(Boolean);
    const matched = keywords.filter(kw => candTitleLower.includes(kw.toLowerCase()));
    if (matched.length > 0) {
      kwPts = Math.min(w.keywords.max, matched.length * w.keywords.per_keyword);
      kwCat = matched.length >= 3 ? 'strong' : 'partial';
      kwDetail = `${matched.length} keyword${matched.length > 1 ? 's' : ''}: ${matched.slice(0, 4).join(', ')}`;
    } else { kwCat = 'none'; kwDetail = 'No keyword overlap'; }
  } else { kwDetail = 'Skipped (no title match base)'; }
  score += kwPts;
  breakdown.push({
    dimension: 'Skills/Keywords', points: kwPts, max_points: kwMax, category: kwCat,
    detail: kwDetail, comparison: { candidate: candTitle, required: role.keywords || '—' },
  });

  // ── 5. INDUSTRY RELEVANCE ──
  let indPts = 0, indMax = w.industry.match, indCat = 'n/a', indDetail = '';
  const vertical = role.vertical || '';
  if (vertical) {
    const deptKws = DEPT_KEYWORDS[vertical] || [];
    const matched = deptKws.find(kw => candDept.includes(kw) || candTitleLower.includes(kw));
    if (matched) {
      indPts = w.industry.match; indCat = 'match';
      indDetail = `Vertical match: ${VERTICAL_LABELS[vertical] || vertical} (via "${matched}")`;
    } else { indCat = 'none'; indDetail = `No ${VERTICAL_LABELS[vertical] || vertical} signals`; }
  } else { indDetail = 'No vertical requirement'; }
  score += indPts;
  breakdown.push({
    dimension: 'Industry Relevance', points: indPts, max_points: indMax, category: indCat,
    detail: indDetail, comparison: { candidate: candDept || 'unknown', required: vertical || 'any' },
  });

  return { score: Math.max(0, Math.min(100, score)), breakdown };
}

// ── Helpers ──

const EMPTY_CANDIDATE = { title: '', city: '', state: '', seniority: '', department: '' };
const SENIORITY_OPTIONS = ['', 'entry', 'junior', 'mid', 'senior', 'manager', 'director', 'vp'];
const VERTICAL_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'accounting_finance', label: 'Accounting & Finance' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'logistics_supply_chain', label: 'Logistics & Supply Chain' },
  { value: 'hr_compliance', label: 'HR & Compliance' },
];

const InputRow = ({ label, value, onChange, placeholder, testId }) => (
  <div className="flex items-center gap-2">
    <label className="text-[10px] font-medium text-gray-500 w-20 text-right flex-shrink-0">{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="flex-1 px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37] bg-white"
      data-testid={testId} />
  </div>
);

const SelectRow = ({ label, value, onChange, options, testId }) => (
  <div className="flex items-center gap-2">
    <label className="text-[10px] font-medium text-gray-500 w-20 text-right flex-shrink-0">{label}</label>
    <select value={value} onChange={e => onChange(e.target.value)}
      className="flex-1 px-2 py-1.5 text-[11px] border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37] bg-white"
      data-testid={testId}>
      {options.map(o => typeof o === 'string'
        ? <option key={o} value={o}>{o || '— Select —'}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  </div>
);

// ── Diff helper ──
function buildTargetingDiff(currentCriteria, simRole) {
  const changes = [];
  const cur = currentCriteria || {};

  // Title / search_titles
  const curTitles = (cur.search_titles || []).join(', ');
  const simTitle = simRole.title || '';
  if (simTitle && simTitle !== cur.role_title) {
    changes.push({
      field: 'Search Titles', current: curTitles || '(auto-detected)',
      recommended: simTitle, reason: 'Simulator title differs from current search targets',
    });
  }

  // Keywords
  const curKw = (cur.title_keywords || []).join(', ');
  const simKw = simRole.keywords || '';
  if (simKw && simKw !== curKw) {
    changes.push({
      field: 'Keywords', current: curKw || '(none)',
      recommended: simKw, reason: 'Keyword targeting updated in simulator',
    });
  }

  // Seniority
  if (simRole.seniority && simRole.seniority !== cur.seniority) {
    changes.push({
      field: 'Seniority', current: cur.seniority || '(any)',
      recommended: simRole.seniority, reason: 'Seniority filter adjusted',
    });
  }

  // Location
  if (simRole.location && simRole.location !== cur.location) {
    changes.push({
      field: 'Location', current: cur.location || '(any)',
      recommended: simRole.location, reason: 'Location targeting updated',
    });
  }

  // Vertical
  if (simRole.vertical && simRole.vertical !== cur.vertical) {
    changes.push({
      field: 'Vertical', current: VERTICAL_LABELS[cur.vertical] || cur.vertical || '(none)',
      recommended: VERTICAL_LABELS[simRole.vertical] || simRole.vertical, reason: 'Vertical focus changed',
    });
  }

  return changes;
}

// ── Main Component ──

export const ScoreSimulator = ({ onClose, existingRole, onTargetingApplied, preset }) => {
  const currentCriteria = existingRole?.criteria || null;

  const [role, setRole] = useState({
    title: preset?.title || existingRole?.title || '',
    location: preset?.location || currentCriteria?.location || existingRole?.location || '',
    seniority: preset?.seniority || currentCriteria?.seniority || '',
    vertical: preset?.vertical || currentCriteria?.vertical || existingRole?.vertical || '',
    keywords: preset?.keywords || (currentCriteria?.title_keywords || []).join(', '),
  });
  const [candidates, setCandidates] = useState([{ ...EMPTY_CANDIDATE, id: 1 }]);
  const [nextId, setNextId] = useState(2);
  const [showTargeting, setShowTargeting] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);

  const updateRole = (field, val) => { setRole(r => ({ ...r, [field]: val })); setApplied(false); };
  const updateCandidate = (idx, field, val) => setCandidates(prev =>
    prev.map((c, i) => i === idx ? { ...c, [field]: val } : c)
  );
  const addCandidate = () => {
    if (candidates.length >= 3) return;
    setCandidates(prev => [...prev, { ...EMPTY_CANDIDATE, id: nextId }]);
    setNextId(n => n + 1);
  };
  const removeCandidate = (idx) => {
    if (candidates.length <= 1) return;
    setCandidates(prev => prev.filter((_, i) => i !== idx));
  };
  const resetAll = () => {
    setRole({ title: '', location: '', seniority: '', vertical: '', keywords: '' });
    setCandidates([{ ...EMPTY_CANDIDATE, id: nextId }]);
    setNextId(n => n + 1); setApplied(false); setShowTargeting(false);
  };

  const results = useMemo(() =>
    candidates.map(c => simulateScore(role, c, DEFAULT_WEIGHTS)),
    [role, candidates]
  );

  const bestIdx = results.length > 1 ? results.reduce((best, r, i) => r.score > results[best].score ? i : best, 0) : -1;
  const bestScore = results.length > 0 ? Math.max(...results.map(r => r.score)) : 0;

  // Targeting diff
  const targetingChanges = useMemo(() => {
    if (!existingRole || !currentCriteria) return [];
    return buildTargetingDiff(currentCriteria, role);
  }, [existingRole, currentCriteria, role]);

  const hasTargetingChanges = targetingChanges.length > 0;

  const applyTargeting = async () => {
    if (!existingRole?.role_id) return;
    setApplying(true);
    try {
      const token = localStorage.getItem('backoffice_token');
      const newCriteria = {};
      if (role.title) newCriteria.search_titles = [role.title];
      if (role.keywords) newCriteria.title_keywords = role.keywords.split(',').map(k => k.trim()).filter(Boolean);
      if (role.seniority) newCriteria.seniority = role.seniority;
      if (role.location) newCriteria.location = role.location;
      if (role.vertical) newCriteria.vertical = role.vertical;

      const res = await fetch(`${API_URL}/api/intelligence/acquisition/roles/${existingRole.role_id}/apply-targeting`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ criteria: newCriteria }),
      });
      const data = await res.json();
      if (data.ok) {
        toast.success(`Targeting updated: ${data.label}`);
        setApplied(true);
        setConfirmApply(false);
        if (onTargetingApplied) onTargetingApplied(data);
      } else {
        toast.error(data.detail || 'Failed to apply targeting');
      }
    } catch { toast.error('Failed to apply targeting'); }
    setApplying(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="score-simulator-overlay">
      <div className="bg-white rounded-2xl shadow-2xl max-w-[900px] w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-[#D4AF37]" />
            <h2 className="text-sm font-bold text-[#0A1F44]">Score Simulator</h2>
            <span className="text-[9px] text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Instant</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={resetAll} className="p-1.5 hover:bg-gray-200 rounded-md text-gray-400" title="Reset all" data-testid="simulator-reset">
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-200 rounded-md text-gray-400" data-testid="simulator-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Role Requirements */}
          <div className="p-3 bg-[#0A1F44]/[0.03] border border-[#0A1F44]/10 rounded-xl space-y-2" data-testid="simulator-role-section">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-bold text-[#0A1F44] uppercase tracking-wider">Role Requirements</span>
              {existingRole && (
                <span className="text-[8px] text-gray-400">
                  Linked: {existingRole.title?.slice(0, 35)}{existingRole.title?.length > 35 ? '...' : ''} @ {existingRole.company}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <InputRow label="Job Title" value={role.title} onChange={v => updateRole('title', v)}
                placeholder="e.g. Logistics Analyst" testId="sim-role-title" />
              <InputRow label="Location" value={role.location} onChange={v => updateRole('location', v)}
                placeholder="e.g. Tampa, Florida" testId="sim-role-location" />
              <SelectRow label="Seniority" value={role.seniority} onChange={v => updateRole('seniority', v)}
                options={SENIORITY_OPTIONS} testId="sim-role-seniority" />
              <SelectRow label="Vertical" value={role.vertical} onChange={v => updateRole('vertical', v)}
                options={VERTICAL_OPTIONS} testId="sim-role-vertical" />
            </div>
            <InputRow label="Keywords" value={role.keywords} onChange={v => updateRole('keywords', v)}
              placeholder="logistics, analyst, supply chain (comma-separated)" testId="sim-role-keywords" />
          </div>

          {/* Candidates Grid */}
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold text-[#0A1F44] uppercase tracking-wider">
              Test Candidates {candidates.length > 1 && `(${candidates.length})`}
            </span>
            {candidates.length < 3 && (
              <button onClick={addCandidate}
                className="flex items-center gap-1 px-2 py-1 text-[9px] font-bold text-[#0A1F44] hover:bg-gray-100 rounded-md border border-dashed border-gray-300"
                data-testid="simulator-add-candidate">
                <Plus className="h-2.5 w-2.5" /> Add Variation
              </button>
            )}
          </div>

          <div className={`grid gap-4 ${candidates.length === 1 ? 'grid-cols-1' : candidates.length === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {candidates.map((c, idx) => {
              const res = results[idx];
              const isBest = bestIdx === idx && candidates.length > 1;
              return (
                <div key={c.id}
                  className={`p-3 rounded-xl border space-y-2.5 transition-all ${isBest ? 'border-emerald-300 bg-emerald-50/30 ring-1 ring-emerald-200' : 'border-gray-200 bg-white'}`}
                  data-testid={`simulator-candidate-${idx}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-bold text-[#0A1F44]">
                        {candidates.length > 1 ? `Variation ${idx + 1}` : 'Candidate'}
                      </span>
                      {isBest && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[7px] font-bold rounded-full">BEST</span>}
                    </div>
                    {candidates.length > 1 && (
                      <button onClick={() => removeCandidate(idx)} className="p-1 hover:bg-red-50 rounded text-gray-300 hover:text-red-400">
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <InputRow label="Title" value={c.title} onChange={v => updateCandidate(idx, 'title', v)}
                      placeholder="Sr. Logistics Analyst" testId={`sim-cand-${idx}-title`} />
                    <div className="grid grid-cols-2 gap-1.5">
                      <InputRow label="City" value={c.city} onChange={v => updateCandidate(idx, 'city', v)}
                        placeholder="Tampa" testId={`sim-cand-${idx}-city`} />
                      <InputRow label="State" value={c.state} onChange={v => updateCandidate(idx, 'state', v)}
                        placeholder="florida" testId={`sim-cand-${idx}-state`} />
                    </div>
                    <SelectRow label="Seniority" value={c.seniority} onChange={v => updateCandidate(idx, 'seniority', v)}
                      options={SENIORITY_OPTIONS} testId={`sim-cand-${idx}-seniority`} />
                    <InputRow label="Dept" value={c.department} onChange={v => updateCandidate(idx, 'department', v)}
                      placeholder="logistics, finance..." testId={`sim-cand-${idx}-dept`} />
                  </div>
                  <div className="pt-2 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] font-medium text-gray-500">Simulated Score</span>
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${res.score >= 50 ? 'bg-emerald-100' : res.score >= 30 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                        <span className={`text-xs font-bold ${res.score >= 50 ? 'text-emerald-700' : res.score >= 30 ? 'text-amber-700' : 'text-gray-600'}`}>{res.score}</span>
                      </div>
                    </div>
                    <ScoreBreakdown breakdown={res.breakdown} totalScore={res.score} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Quick Comparison */}
          {candidates.length > 1 && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl" data-testid="simulator-comparison">
              <span className="text-[10px] font-bold text-[#0A1F44] uppercase tracking-wider">Quick Comparison</span>
              <div className="mt-2 flex gap-4">
                {candidates.map((c, idx) => {
                  const res = results[idx];
                  const label = c.title || `Variation ${idx + 1}`;
                  return (
                    <div key={c.id} className="flex-1 flex items-center gap-2">
                      <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${res.score >= 50 ? 'bg-emerald-100' : res.score >= 30 ? 'bg-amber-100' : 'bg-gray-100'}`}>
                        <span className={`text-[9px] font-bold ${res.score >= 50 ? 'text-emerald-700' : res.score >= 30 ? 'text-amber-700' : 'text-gray-600'}`}>{res.score}</span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] font-medium text-[#0A1F44] truncate">{label}</p>
                        <div className="h-1 bg-gray-200 rounded-full mt-0.5 w-24">
                          <div className={`h-full rounded-full ${res.score >= 50 ? 'bg-emerald-500' : res.score >= 30 ? 'bg-amber-400' : 'bg-gray-300'}`}
                            style={{ width: `${res.score}%` }} />
                        </div>
                      </div>
                      {bestIdx === idx && <span className="text-[7px] font-bold text-emerald-600">BEST</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Auto-Targeting Section ── */}
          {existingRole && bestScore > 0 && (
            <div className="border-t border-dashed border-gray-300 pt-4" data-testid="auto-targeting-section">
              <button onClick={() => setShowTargeting(s => !s)}
                className="flex items-center gap-2 text-[11px] font-bold text-[#0A1F44] hover:text-[#D4AF37] transition-colors"
                data-testid="auto-targeting-toggle">
                <Target className="h-3.5 w-3.5" />
                Recommended Targeting
                {hasTargetingChanges && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[7px] font-bold rounded-full">{targetingChanges.length} changes</span>}
                {applied && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[7px] font-bold rounded-full flex items-center gap-0.5"><Check className="h-2 w-2" />Applied</span>}
              </button>

              {showTargeting && (
                <div className="mt-3 space-y-3">
                  {!hasTargetingChanges ? (
                    <p className="text-[10px] text-gray-400 italic">No changes — simulator criteria match current targeting.</p>
                  ) : (
                    <>
                      {/* Changes comparison */}
                      <div className="space-y-2" data-testid="targeting-diff">
                        {targetingChanges.map((ch, i) => (
                          <div key={i} className="p-2.5 bg-white border border-gray-200 rounded-lg">
                            <div className="flex items-center gap-1.5 mb-1">
                              <span className="text-[9px] font-bold text-[#0A1F44] uppercase tracking-wider">{ch.field}</span>
                              <span className="text-[8px] text-gray-400">{ch.reason}</span>
                            </div>
                            <div className="flex items-center gap-2 text-[10px]">
                              <div className="flex-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-red-600 line-through truncate" data-testid={`targeting-current-${i}`}>
                                {ch.current}
                              </div>
                              <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                              <div className="flex-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded text-emerald-700 font-medium truncate" data-testid={`targeting-recommended-${i}`}>
                                {ch.recommended}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Expected impact */}
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-[9px] text-blue-700">
                          <strong>Expected impact:</strong> Next sourcing run will use optimized criteria. Best simulated score: <strong>{bestScore}/100</strong>.
                          Candidates will be labeled <em>"Optimized Targeting"</em>.
                        </p>
                      </div>

                      {/* Apply button / confirmation */}
                      {applied ? (
                        <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="targeting-applied-confirmation">
                          <Check className="h-4 w-4 text-emerald-600" />
                          <p className="text-[10px] text-emerald-700 font-medium">Targeting applied. Next sourcing run will use optimized criteria.</p>
                        </div>
                      ) : confirmApply ? (
                        <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg space-y-2" data-testid="targeting-confirm-dialog">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                            <p className="text-[10px] font-bold text-amber-800">Confirm Targeting Change</p>
                          </div>
                          <p className="text-[9px] text-amber-700">
                            This will update the search criteria for <strong>{existingRole.title}</strong> at <strong>{existingRole.company}</strong>.
                            The next sourcing run will use these optimized parameters.
                          </p>
                          <div className="flex items-center gap-2">
                            <button onClick={applyTargeting} disabled={applying}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A1F44] text-white text-[10px] font-bold rounded-md hover:bg-[#0d2a5c] disabled:opacity-50"
                              data-testid="targeting-confirm-btn">
                              {applying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                              Yes, Apply Targeting
                            </button>
                            <button onClick={() => setConfirmApply(false)}
                              className="px-3 py-1.5 text-gray-500 text-[10px] font-medium rounded-md hover:bg-gray-100"
                              data-testid="targeting-cancel-btn">
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmApply(true)}
                          className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#D4AF37] to-[#C4A030] text-[#0A1F44] text-[10px] font-bold rounded-lg hover:shadow-md transition-all"
                          data-testid="apply-targeting-btn">
                          <Target className="h-3 w-3" /> Apply Recommended Targeting
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
